import { useState, useCallback, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { AIService } from '../../../../../services'
import { useSaveQueue } from '../../../../../hooks/useSaveQueue'
import type { Node, Edge } from '@xyflow/react'
import type { FeedbackContext, ChatTarget } from '../../../../../context/ScopedChatContext'
import type { ReactFlowNodeData } from '../../../../../types/mindmap-components'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'

/**
 * Chat message structure for AI conversations
 */
export interface ChatMessage {
  id: string
  content: string
  sender: 'user' | 'ai'
  timestamp: Date
}

/**
 * Selected chat person - a specific stakeholder
 */
export type SelectedChatPerson = {
  type: 'stakeholder'
  id?: string
  name: string
  role?: string
} | null

/**
 * Configuration for the useChatWindow hook
 */
interface UseChatWindowConfig {
  mindmapId: string | undefined
  nodes: Node<ReactFlowNodeData>[]
  edges: Edge[]
  onAddNodes?: (nodes: MindMapNode[], edges: MindMapEdge[]) => void
}

/**
 * Return type for the useChatWindow hook
 */
export interface UseChatWindowReturn {
  // State
  isChatVisible: boolean
  chatMessages: ChatMessage[]
  isChatLoading: boolean
  feedbackContext: FeedbackContext | null

  // Derived stakeholder info (from selected chat person)
  stakeholderName: string | null
  stakeholderRole: string | null

  // Handlers
  openScopedChat: (target: ChatTarget, context: FeedbackContext) => void
  openGeneralChat: (target: ChatTarget) => void
  closeChat: () => void
  sendMessage: (message: string) => Promise<void>
}

/**
 * Custom hook for managing the scoped chat window state and operations.
 * Encapsulates all chat-related logic that was previously in MindMapFlow.
 *
 * Features:
 * - Manages chat visibility, messages, and loading state
 * - Handles opening scoped chats with specific feedback contexts
 * - Sends messages to the AI service with mindmap context
 */
export function useChatWindow({
  mindmapId,
  nodes,
  edges,
  onAddNodes,
}: UseChatWindowConfig): UseChatWindowReturn {
  const saveQueue = useSaveQueue()

  // Chat visibility state
  const [isChatVisible, setIsChatVisible] = useState(false)

  // Chat messages
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])

  // Loading state
  const [isChatLoading, setIsChatLoading] = useState(false)

  // Selected chat target
  const [selectedChatPerson, setSelectedChatPerson] = useState<SelectedChatPerson>(null)

  // Feedback context for scoped conversations
  const [feedbackContext, setFeedbackContext] = useState<FeedbackContext | null>(null)

  // Derive stakeholder info from selected chat person
  const stakeholderName = useMemo(
    (): string | null => selectedChatPerson?.name ?? null,
    [selectedChatPerson]
  )

  const stakeholderRole = useMemo(
    (): string | null => selectedChatPerson?.role ?? null,
    [selectedChatPerson]
  )

  /**
   * Opens a scoped chat about a specific feedback point.
   * Called from StakeholderRatingsFooter when user clicks on a feedback section.
   */
  const openScopedChat = useCallback((target: ChatTarget, context: FeedbackContext) => {
    setSelectedChatPerson({
      type: 'stakeholder',
      id: target.id,
      name: target.name,
      role: target.role,
    })

    // Set the feedback context
    setFeedbackContext(context)

    // Open chat and clear previous messages
    setIsChatVisible(true)
    setChatMessages([])
  }, [])

  /**
   * Opens a general chat with a stakeholder (no specific feedback context).
   * Called from the sidebar when user clicks chat button next to a stakeholder.
   * Uses all mindmap nodes as context.
   */
  const openGeneralChat = useCallback((target: ChatTarget) => {
    setSelectedChatPerson({
      type: 'stakeholder',
      id: target.id,
      name: target.name,
      role: target.role,
    })

    // Clear feedback context for general chat
    setFeedbackContext(null)

    // Open chat and clear previous messages
    setIsChatVisible(true)
    setChatMessages([])
  }, [])

  /**
   * Closes the chat window.
   */
  const closeChat = useCallback(() => {
    setIsChatVisible(false)
  }, [])

  /**
   * Sends a chat message to the AI assistant.
   * Waits for any pending saves to complete before sending.
   * Includes current mindmap context (nodes and edges) with the message.
   * If the AI response includes nodes/edges, automatically adds them to the mindmap.
   */
  const sendMessage = useCallback(
    async (message: string) => {
      if (!mindmapId) return

      // Add user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        content: message,
        sender: 'user',
        timestamp: new Date(),
      }
      setChatMessages((prev) => [...prev, userMessage])
      setIsChatLoading(true)

      try {
        // Wait for any pending saves to complete before sending chat message
        await saveQueue.waitForPendingSaves()

        // Prepare context with current mindmap state
        const context = {
          nodes: nodes.map((node) => ({
            id: node.id,
            label: node.data.label,
            category: node.data.category,
            position: node.position,
          })),
          edges: edges.map((edge) => ({
            id: edge.id,
            source: edge.source,
            target: edge.target,
          })),
        }

        const stakeholderId = selectedChatPerson?.id
        const response = await AIService.sendChatMessage(
          mindmapId,
          message,
          stakeholderId,
          context,
          feedbackContext ?? undefined
        )

        if (response.error) {
          throw new Error(response.error.message)
        }

        // Extract response text - handle both plain strings and JSON stringified objects
        let responseText: string = 'Sorry, I could not process your message.'
        const rawResponse = response.data?.response

        // Handle different response formats
        if (rawResponse) {
          if (typeof rawResponse === 'object') {
            // If it's already an object with a 'response' property, extract it
            const responseObj = rawResponse as { response?: string }
            if ('response' in responseObj && typeof responseObj.response === 'string') {
              responseText = responseObj.response
            } else {
              // If it's an object but no 'response' property, stringify it
              responseText = JSON.stringify(rawResponse)
            }
          } else if (typeof rawResponse === 'string') {
            // Try to parse as JSON if it's a string
            try {
              const parsed = JSON.parse(rawResponse)
              // If it's an object with a 'response' property, extract it
              if (
                parsed &&
                typeof parsed === 'object' &&
                parsed !== null &&
                'response' in parsed &&
                typeof parsed.response === 'string'
              ) {
                responseText = parsed.response
              } else {
                // Not the expected format, use the string as-is
                responseText = rawResponse
              }
            } catch {
              // Not JSON, use as-is
              responseText = rawResponse
            }
          } else {
            // Fallback: convert to string
            responseText = String(rawResponse)
          }
        }

        // Add AI response
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          content: responseText,
          sender: 'ai',
          timestamp: new Date(),
        }
        setChatMessages((prev) => [...prev, aiMessage])

        // If AI returned nodes/edges, add them to the mindmap
        if (response.data?.nodes && response.data.edges && onAddNodes) {
          onAddNodes(response.data.nodes, response.data.edges)
        }
      } catch (error) {
        console.error('Chat error:', error)
        Sentry.captureException(error, {
          tags: { feature: 'mindmap.chat', action: 'send_message' },
        })
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          content: 'Sorry, there was an error processing your message. Please try again.',
          sender: 'ai',
          timestamp: new Date(),
        }
        setChatMessages((prev) => [...prev, errorMessage])
      } finally {
        setIsChatLoading(false)
      }
    },
    [mindmapId, nodes, edges, onAddNodes, saveQueue, selectedChatPerson, feedbackContext]
  )

  return {
    // State
    isChatVisible,
    chatMessages,
    isChatLoading,
    feedbackContext,

    // Derived stakeholder info
    stakeholderName,
    stakeholderRole,

    // Handlers
    openScopedChat,
    openGeneralChat,
    closeChat,
    sendMessage,
  }
}
